import { useRef, useEffect, useCallback } from 'react';

export function readStoredScroll(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    const n = Number(JSON.parse(raw));
    return Number.isFinite(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

export function writeStoredScroll(key, scrollTop) {
  if (!Number.isFinite(scrollTop) || scrollTop < 0) return;
  localStorage.setItem(key, JSON.stringify(scrollTop));
}

export function readStoredScrollMap(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function writeStoredScrollMap(key, map) {
  localStorage.setItem(key, JSON.stringify(map));
}

/**
 * Persist and restore scrollTop for a scrollable element via localStorage.
 *
 * @param {string} storageKey
 * @param {{ enabled?: boolean, skipRestore?: boolean, debounceMs?: number, mapKey?: string, restoreDeps?: unknown[] }} [options]
 */
export function usePersistedScroll(storageKey, options = {}) {
  const { enabled = true, skipRestore = false, debounceMs = 200, mapKey, restoreDeps = [] } = options;
  const elRef = useRef(null);
  const hydratedRef = useRef(false);
  const timerRef = useRef(null);

  const persist = useCallback(() => {
    const el = elRef.current;
    if (!el || !enabled) return;
    if (mapKey) {
      const map = readStoredScrollMap(storageKey);
      map[mapKey] = el.scrollTop;
      writeStoredScrollMap(storageKey, map);
    } else {
      writeStoredScroll(storageKey, el.scrollTop);
    }
  }, [storageKey, enabled, mapKey]);

  useEffect(() => {
    hydratedRef.current = false;
  }, restoreDeps);

  useEffect(() => {
    if (!enabled) return;
    // Caller will position the scroller (e.g. scroll-to-selected); don't fight it.
    if (skipRestore) {
      hydratedRef.current = true;
      return;
    }

    const tryRestore = () => {
      if (hydratedRef.current) return;
      const el = elRef.current;
      if (!el) return;

      let top = null;
      if (mapKey) {
        const map = readStoredScrollMap(storageKey);
        const v = Number(map[mapKey]);
        top = Number.isFinite(v) && v >= 0 ? v : null;
      } else {
        top = readStoredScroll(storageKey);
      }

      if (top == null) {
        hydratedRef.current = true;
        return;
      }

      const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = Math.min(top, maxTop);
      hydratedRef.current = true;
    };

    tryRestore();
    const raf1 = requestAnimationFrame(() => {
      tryRestore();
      requestAnimationFrame(tryRestore);
    });
    return () => cancelAnimationFrame(raf1);
  }, [storageKey, enabled, skipRestore, mapKey, ...restoreDeps]);

  useEffect(() => {
    const el = elRef.current;
    if (!el || !enabled) return;

    const onScroll = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(persist, debounceMs);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      clearTimeout(timerRef.current);
    };
  }, [enabled, persist, debounceMs, ...restoreDeps]);

  return elRef;
}
